import { Link } from 'react-router-dom';
import { CodeSearchResult } from '../../types/code.types';
import toast from 'react-hot-toast';

interface CodeCardProps {
  code: CodeSearchResult;
}

const TYPE_STYLES: Record<string, { bg: string; text: string }> = {
  ICD10:    { bg: 'bg-teal-50',   text: 'text-teal-700' },
  ICD10CM:  { bg: 'bg-teal-50',   text: 'text-teal-700' },
  ICD10PCS: { bg: 'bg-blue-50',   text: 'text-blue-700' },
  HCPCS:    { bg: 'bg-violet-50', text: 'text-violet-700' },
  NDC:      { bg: 'bg-amber-50',  text: 'text-amber-700' },
  LOINC:    { bg: 'bg-green-50',  text: 'text-green-700' },
  SNOMED:   { bg: 'bg-rose-50',   text: 'text-rose-700' },
  RXNORM:   { bg: 'bg-indigo-50', text: 'text-indigo-700' },
};

const CodeCard = ({ code }: CodeCardProps) => {
  const typeKey = code.codeType?.toUpperCase().replace(/-/g, '') || '';
  const style = TYPE_STYLES[typeKey] || { bg: 'bg-gray-50', text: 'text-gray-700' };
  const score = code.confidenceScore || 0;

  const copyCode = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(code.code);
    toast.success(`${code.code} copied!`, { duration: 1500, position: 'bottom-right' });
  };

  return (
    <Link
      to={`/code/${code.codeType}/${code.code}`}
      className="bg-white border border-gray-100 rounded-xl px-4 py-3.5 flex items-center gap-4 hover:border-teal-200 hover:shadow-card transition-all group block"
    >
      {/* Billable indicator */}
      <div className="flex-shrink-0">
        {code.billable ? (
          <span className="w-1.5 h-1.5 rounded-full bg-teal-400 block" title="Billable" />
        ) : (
          <span className="w-1.5 h-1.5 rounded-full bg-gray-200 block" />
        )}
      </div>

      {/* Code badge */}
      <span className={`font-mono text-[11px] font-bold px-2.5 py-1 rounded-lg flex-shrink-0 min-w-[76px] text-center ${style.bg} ${style.text}`}>
        {code.code}
      </span>

      {/* Description */}
      <div className="flex-1 min-w-0">
        <p className="text-[13px] font-semibold text-gray-800 truncate group-hover:text-teal-700 transition-colors">
          {code.description}
        </p>
        {code.category && (
          <p className="text-[11px] text-gray-400 truncate mt-0.5">{code.category}</p>
        )}
      </div>

      {/* Type tag */}
      <span className="text-[10px] text-gray-400 bg-gray-50 border border-gray-100 px-2 py-0.5 rounded-full flex-shrink-0 hidden lg:inline font-medium">
        {code.codeType}
      </span>

      {/* Score bar */}
      {score > 0 && (
        <div className="flex items-center gap-2 flex-shrink-0 hidden xl:flex">
          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full transition-all ${
                score >= 80 ? 'bg-teal-500' : score >= 50 ? 'bg-amber-400' : 'bg-gray-300'
              }`}
              style={{ width: `${score}%` }}
            />
          </div>
          <span className="text-[10px] text-gray-400 font-mono w-7 text-right">{score}%</span>
        </div>
      )}

      {/* Action buttons */}
      <div className="flex items-center gap-1 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={copyCode}
          className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all"
          title="Copy code"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
          </svg>
        </button>
        <div className="p-1.5 text-gray-400 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-all">
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="m9 18 6-6-6-6"/>
          </svg>
        </div>
      </div>
    </Link>
  );
};

export default CodeCard;